export type DemoKeySide = 'left' | 'right';

export interface DemoInputSample {
  readonly leftDown: boolean;
  readonly rightDown: boolean;
  readonly fireDown: boolean;
  readonly lastSide: DemoKeySide | null;
  readonly inputSequence: number | null;
  readonly fireSequence: string | null;
  readonly hasFire: boolean;
}

interface DemoInputMemory {
  readonly inputSequence: number | null;
  readonly fireSequence: string | null;
  readonly leftUntil: number;
  readonly rightUntil: number;
  readonly fireUntil: number;
}

export interface DemoInputFrame {
  readonly left: boolean;
  readonly right: boolean;
  readonly fire: boolean;
  readonly memory: DemoInputMemory;
}

type UnknownRecord = Record<string, unknown>;

const EMPTY_SAMPLE: DemoInputSample = Object.freeze({
  leftDown: false,
  rightDown: false,
  fireDown: false,
  lastSide: null,
  inputSequence: null,
  fireSequence: null,
  hasFire: false,
});

const EMPTY_MEMORY: DemoInputMemory = Object.freeze({
  inputSequence: null,
  fireSequence: null,
  leftUntil: 0,
  rightUntil: 0,
  fireUntil: 0,
});

const BRIDGE_BY_EVENT = Object.freeze({
  '100m': '__SPRINT_100M__',
  'long-jump': '__LONG_JUMP__',
  'shot-put': '__SHOT_PUT__',
  'high-jump': '__HIGH_JUMP__',
  '400m': '__FOUR_HUNDRED__',
  '110m-hurdles': '__HURDLES__',
  discus: '__DISCUS__',
  'pole-vault': '__POLE_VAULT__',
  javelin: '__JAVELIN__',
  '1500m': '__FIFTEEN_HUNDRED__',
} as const);

const asRecord = (value: unknown): UnknownRecord | null =>
  typeof value === 'object' && value !== null ? value as UnknownRecord : null;

const bool = (record: UnknownRecord | null, key: string): boolean =>
  record?.[key] === true;

const safeInteger = (record: UnknownRecord | null, key: string): number | null => {
  const value = record?.[key];
  return Number.isSafeInteger(value) && Number(value) >= 0 ? Number(value) : null;
};

const side = (record: UnknownRecord | null, key: string): DemoKeySide | null => {
  const value = record?.[key];
  return value === 'left' || value === 'right' ? value : null;
};

const heldSample = (
  held: UnknownRecord | null,
  hasFire: boolean,
): Pick<DemoInputSample, 'leftDown' | 'rightDown' | 'fireDown' | 'hasFire'> => ({
  leftDown: bool(held, 'leftDown'),
  rightDown: bool(held, 'rightDown'),
  fireDown: hasFire && (bool(held, 'fireHeld') || bool(held, 'fireDown')),
  hasFire,
});

const marker = (
  snapshot: UnknownRecord | null,
  field: string,
): string | null => {
  const value = snapshot?.[field];
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const attempt = safeInteger(snapshot, 'attempt') ?? 0;
  return `${attempt}:${field}:${value}`;
};

/** Converts each event's public telemetry shape into the three teaching keys. */
export const demoInputSample = (
  eventId: string,
  value: unknown,
): DemoInputSample => {
  const snapshot = asRecord(value);
  if (!snapshot) return EMPTY_SAMPLE;

  if (eventId === '100m') {
    return {
      ...heldSample(null, false),
      lastSide: side(snapshot, 'lastSide'),
      inputSequence: safeInteger(snapshot, 'totalInputs'),
      fireSequence: null,
    };
  }

  if (eventId === 'long-jump' || eventId === 'shot-put') {
    return {
      ...heldSample(null, true),
      lastSide: side(snapshot, 'lastSide'),
      inputSequence: safeInteger(snapshot, 'totalInputs'),
      fireSequence: marker(
        snapshot,
        eventId === 'long-jump' ? 'takeoffPosition' : 'releasePosition',
      ),
    };
  }

  if (eventId === 'high-jump' || eventId === '400m') {
    const hasFire = eventId === 'high-jump';
    return {
      leftDown: bool(snapshot, 'leftDown'),
      rightDown: bool(snapshot, 'rightDown'),
      fireDown: hasFire && bool(snapshot, 'fireDown'),
      lastSide: side(snapshot, eventId === 'high-jump' ? 'lastDirection' : 'sampledDirection'),
      inputSequence: safeInteger(snapshot, eventId === 'high-jump' ? 'totalInputs' : 'totalSamples'),
      fireSequence: null,
      hasFire,
    };
  }

  if (eventId === '110m-hurdles') {
    const player = asRecord(snapshot.player);
    const held = asRecord(player?.heldInput);
    return {
      ...heldSample(held, true),
      lastSide: side(player, 'sampledDirection'),
      inputSequence: safeInteger(player, 'totalSamples'),
      fireSequence: null,
    };
  }

  if (eventId === '1500m') {
    const runners = Array.isArray(snapshot.runners) ? snapshot.runners : [];
    const human = runners.map(asRecord).find((runner) =>
      asRecord(runner?.participant)?.kind === 'human') ?? null;
    const held = asRecord(human?.heldInput);
    return {
      ...heldSample(held, false),
      lastSide: side(human, 'sampledDirection'),
      inputSequence: safeInteger(human, 'rewardedSamples'),
      fireSequence: null,
    };
  }

  const hasFire = eventId === 'discus' || eventId === 'pole-vault' || eventId === 'javelin';
  const held = asRecord(snapshot.heldInput);
  return {
    ...heldSample(held, hasFire),
    lastSide: side(snapshot, 'sampledDirection'),
    inputSequence: safeInteger(snapshot, 'totalSamples'),
    fireSequence: null,
  };
};

/** Holds impulse-style inputs on screen long enough for a player to see them. */
export const advanceDemoInputFrame = (
  sample: DemoInputSample,
  previous: DemoInputMemory = EMPTY_MEMORY,
  now = performance.now(),
): DemoInputFrame => {
  let leftUntil = previous.leftUntil;
  let rightUntil = previous.rightUntil;
  let fireUntil = previous.fireUntil;

  if (
    sample.inputSequence !== null &&
    sample.inputSequence !== previous.inputSequence &&
    sample.inputSequence > 0
  ) {
    if (sample.lastSide === 'left') {
      leftUntil = now + 180;
      rightUntil = now;
    }
    if (sample.lastSide === 'right') {
      rightUntil = now + 180;
      leftUntil = now;
    }
  }
  if (
    sample.fireSequence !== null &&
    sample.fireSequence !== previous.fireSequence
  ) {
    fireUntil = now + 260;
  }
  if (sample.fireDown) fireUntil = Math.max(fireUntil, now + 260);

  return {
    left: sample.leftDown || leftUntil > now,
    right: sample.rightDown || rightUntil > now,
    fire: sample.fireDown || fireUntil > now,
    memory: {
      inputSequence: sample.inputSequence,
      fireSequence: sample.fireSequence,
      leftUntil,
      rightUntil,
      fireUntil,
    },
  };
};

const snapshotFor = (eventId: string): unknown => {
  const bridgeName = BRIDGE_BY_EVENT[eventId as keyof typeof BRIDGE_BY_EVENT];
  if (!bridgeName) return null;
  const bridge = asRecord((window as unknown as UnknownRecord)[bridgeName]);
  const getterName = eventId === '1500m' ? 'getRaceSnapshot' : 'getSnapshot';
  const getter = bridge?.[getterName];
  if (typeof getter !== 'function') return null;
  try {
    return getter.call(bridge);
  } catch {
    return null;
  }
};

const key = (name: 'left' | 'fire' | 'right', glyph: string, label: string): HTMLElement => {
  const element = document.createElement('kbd');
  element.dataset.demoKey = name;
  element.innerHTML = `<b>${glyph}</b><span>${label}</span>`;
  return element;
};

/** Prominent read-only key visualizer for direct event Demo Runs. */
export const mountEventDemoInputDisplay = (eventId: string): (() => void) => {
  document.querySelector('[data-event-demo-keys]')?.remove();

  const display = document.createElement('section');
  display.className = 'event-demo-keys';
  display.dataset.eventDemoKeys = '';
  display.hidden = true;
  display.setAttribute('aria-label', 'Live keys used by the automated demo run');
  display.innerHTML = '<small>DEMO INPUT · COPY THE RHYTHM</small>';
  display.append(
    key('left', '←', 'A / LEFT'),
    key('fire', '●', 'SPACE / FIRE'),
    key('right', '→', 'D / RIGHT'),
  );
  document.body.append(display);

  const elements = {
    left: display.querySelector<HTMLElement>('[data-demo-key="left"]')!,
    fire: display.querySelector<HTMLElement>('[data-demo-key="fire"]')!,
    right: display.querySelector<HTMLElement>('[data-demo-key="right"]')!,
  };
  let memory = EMPTY_MEMORY;
  const update = (): void => {
    const queryDemo = new URLSearchParams(window.location.search).get('demo') === '1';
    const inPageDemo = Boolean(document.querySelector(
      '.game-shell[data-demo="true"], .game-shell[data-demo-requested="true"], .demo-badge.is-visible',
    ));
    display.hidden = !(queryDemo || inPageDemo);
    if (display.hidden) return;

    const sample = demoInputSample(eventId, snapshotFor(eventId));
    const frame = advanceDemoInputFrame(sample, memory);
    memory = frame.memory;
    display.dataset.hasFire = String(sample.hasFire);
    for (const name of ['left', 'fire', 'right'] as const) {
      elements[name].classList.toggle('is-pressed', frame[name]);
      elements[name].dataset.pressed = String(frame[name]);
    }
    display.setAttribute(
      'aria-label',
      `Live Demo Run keys: left ${frame.left ? 'pressed' : 'released'}, ${
        sample.hasFire ? `Fire ${frame.fire ? 'pressed' : 'released'}, ` : ''
      }right ${frame.right ? 'pressed' : 'released'}`,
    );
  };

  update();
  const interval = window.setInterval(update, 32);
  return () => {
    window.clearInterval(interval);
    display.remove();
  };
};
